import React from "react";
import { motion } from "framer-motion";

export const roleMeta = {
  athletic_director: { label: "Athletic Director", short: "AD", color: "#FF3B30" },
  coach: { label: "Coach", short: "Coach", color: "#FF9F0A" },
  teacher: { label: "Teacher", short: "Teacher", color: "#0A84FF" },
  athlete: { label: "Athlete", short: "Athlete", color: "#34C759" },
  parent: { label: "Parent", short: "Parent", color: "#BF5AF2" },
};

export function PageHeader({ eyebrow, title, accent, children, icon: Icon }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
      <div>
        {eyebrow && (
          <div className="text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-1 flex items-center gap-2">
            {Icon && <Icon size={14} weight="bold" color="#FF3B30" />} {eyebrow}
          </div>
        )}
        <h1 className="font-head text-4xl md:text-5xl font-black uppercase tracking-tighter leading-none">
          {title} {accent && <span className="text-[#FF3B30]">{accent}</span>}
        </h1>
      </div>
      {children}
    </div>
  );
}

export function Card({ children, className = "", delay = 0, ...props }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
      className={`bg-[#141414] border border-white/10 rounded-sm ${className}`} {...props}>
      {children}
    </motion.div>
  );
}

export function StatTile({ label, value, unit, color = "#FFFFFF", testid }) {
  return (
    <Card className="p-5 md:p-6" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[#737373] font-semibold mb-2">{label}</div>
      <div className="font-mono text-3xl md:text-4xl font-black tracking-tighter" style={{ color }}>
        {value}{unit && <span className="text-base text-[#737373] ml-1">{unit}</span>}
      </div>
    </Card>
  );
}

export function RoleBadge({ role }) {
  const m = roleMeta[role] || { label: role, color: "#A3A3A3" };
  return (
    <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-sm"
      style={{ background: `${m.color}1a`, color: m.color }}>{m.label}</span>
  );
}

export function EligibilityBadge({ eligible, testid }) {
  const ok = eligible !== false;
  const c = ok ? "#34C759" : "#FF3B30";
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-sm"
      style={{ background: `${c}1a`, color: c }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: c }} />
      {ok ? "Eligible" : "Ineligible"}
    </span>
  );
}

export function Avatar({ name, size = 40, color = "#FF3B30" }) {
  const initials = (name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="flex items-center justify-center rounded-sm font-head font-bold shrink-0"
      style={{ width: size, height: size, background: `${color}22`, color, fontSize: size * 0.4 }}>
      {initials}
    </div>
  );
}

export function Modal({ open, onClose, title, children, testid }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid={testid}>
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-lg bg-[#141414] border border-white/10 rounded-sm max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[#141414]">
          <h3 className="font-head text-xl font-bold uppercase tracking-tight">{title}</h3>
          <button onClick={onClose} className="text-[#737373] hover:text-white text-2xl leading-none" data-testid="modal-close">×</button>
        </div>
        <div className="p-6">{children}</div>
      </motion.div>
    </div>
  );
}

export const inputCls = "w-full bg-[#0A0A0A] border border-white/20 text-white focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] rounded-sm p-3 placeholder-gray-600 outline-none transition-colors";
export const labelCls = "block text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-2";
export const btnPrimary = "inline-flex items-center justify-center gap-2 bg-[#FF3B30] hover:bg-[#FF5C53] disabled:opacity-60 text-white font-bold uppercase tracking-widest px-6 py-3 rounded-sm transition-colors";
export const btnGhost = "inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white hover:bg-white/5 text-white font-bold uppercase tracking-widest px-6 py-3 rounded-sm transition-colors";

export function Loading({ label = "Loading…" }) {
  return <div className="px-4 md:px-8 py-12 text-[#737373]" data-testid="loading">{label}</div>;
}

export function fmtDate(iso, opts) {
  if (!iso) return "";
  const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
  return d.toLocaleDateString("en-US", opts || { month: "short", day: "numeric" });
}
export function fmtDateTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
